import type { Building } from "@types"

/**
 * Feature GeoJSON parcelle pour {@link BaseMap} : `parcelStyle: "selected"` → remplissage vert.
 */
export type CadastreMapFeature = {
    type: "Feature"
    geometry: {
        type: "Polygon" | "MultiPolygon"
        coordinates: unknown
    }
    properties: { parcelStyle: "selected"; name: string }
}

type CadastreGeometry = {
    type: "Polygon" | "MultiPolygon"
    coordinates: unknown
}

/**
 * Géométrie parcelle renvoyée par l’API (GET bâtiment) : `geometry.cadastre` prioritaire, sinon `infos.cadastre`.
 */
export function getBuildingCadastreLocationRaw(
    b: Building | null | undefined
): unknown {
    if (!b) return null
    const g = b.geometry?.cadastre?.location
    if (g != null) return g
    return b.infos?.cadastre?.location ?? null
}

/**
 * GeoJSON: une position = [lon, lat].
 * `true` ssi paire de nombres finis.
 */
function isValidLonLatPair(p: unknown): boolean {
    return (
        Array.isArray(p) &&
        p.length >= 2 &&
        Number.isFinite(p[0] as number) &&
        Number.isFinite(p[1] as number)
    )
}

function isValidRing(ring: unknown): boolean {
    if (!Array.isArray(ring)) return false
    for (const p of ring) {
        if (!isValidLonLatPair(p)) return false
    }
    return ring.length > 0
}

function isValidPolygonCoordinates(coords: unknown): boolean {
    if (!Array.isArray(coords) || coords.length === 0) return false
    for (const ring of coords) {
        if (!isValidRing(ring)) return false
    }
    return true
}

function isValidMultiPolygonCoordinates(coords: unknown): boolean {
    if (!Array.isArray(coords) || coords.length === 0) return false
    for (const poly of coords) {
        if (!isValidPolygonCoordinates(poly)) return false
    }
    return true
}

/**
 * L’API métier a pu stocker un `type: "MultiPolygon"` dont `coordinates` a été
 * aplatis d’un cran : la structure n’a plus 4 niveaux, Leaflet ne peut plus parser.
 * Si le 1er anneau du 1er poly devrait être une paire [lon, lat] mais on tombe
 * sur un `number`, on réinjecte le niveau « un polygone ».
 */
function normalizeMultiPolygonShell(coords: unknown): unknown {
    if (!Array.isArray(coords) || coords.length === 0) return coords
    const c0 = coords[0] as unknown
    const c0a = Array.isArray(c0) ? c0 : null
    const c1 = c0a && c0a.length > 0 ? c0a[0] : null
    const c1a = Array.isArray(c1) && c1.length > 0 ? c1[0] : null
    const t = c1a
    if (typeof t === "number" && !Array.isArray(t)) {
        return [coords]
    }
    return coords
}

/**
 * Géométrie GeoJSON saine pour BaseMap/Leaflet, ou `null` si invalide / non fiable.
 */
function normalizeBuildingCadastreGeometry(
    geometry: CadastreGeometry
): CadastreGeometry | null {
    if (geometry.type === "MultiPolygon") {
        const fixed = {
            type: "MultiPolygon" as const,
            coordinates: normalizeMultiPolygonShell(geometry.coordinates),
        }
        if (!isValidMultiPolygonCoordinates(fixed.coordinates)) return null
        return fixed
    }
    if (geometry.type === "Polygon") {
        if (!isValidPolygonCoordinates(geometry.coordinates)) return null
        return geometry
    }
    return null
}

/**
 * Extrait `Geometry` depuis la valeur Mongo / GeoJSON, puis normalise.
 */
function geometryFromCadastreLocation(
    raw: unknown,
    label: string
): CadastreMapFeature | null {
    if (raw == null || typeof raw !== "object") return null
    const o = raw as Record<string, unknown>

    let g: { type: string; coordinates: unknown } | null = null
    if (o.type === "Feature" && o.geometry && typeof o.geometry === "object") {
        const geo = o.geometry as { type?: string; coordinates?: unknown }
        if (geo.type === "Polygon" || geo.type === "MultiPolygon") {
            g = { type: geo.type, coordinates: geo.coordinates }
        }
    } else if (o.type === "Polygon" || o.type === "MultiPolygon") {
        g = { type: o.type, coordinates: o.coordinates }
    }
    if (!g) return null

    const normalized = normalizeBuildingCadastreGeometry(g as CadastreGeometry)
    if (!normalized) return null

    return {
        type: "Feature",
        geometry: {
            type: normalized.type,
            coordinates: normalized.coordinates,
        },
        properties: { parcelStyle: "selected", name: label },
    }
}

/**
 * Convertit la donnée `location` (JSON éventuel, Feature ou Geometry) en feature affichable sur la carte.
 */
export function toCadastreParcelFeature(
    raw: unknown,
    label: string
): CadastreMapFeature | null {
    if (raw == null) return null
    if (typeof raw === "string") {
        try {
            const parsed = JSON.parse(raw) as unknown
            return geometryFromCadastreLocation(parsed, label)
        } catch {
            return null
        }
    }
    return geometryFromCadastreLocation(raw, label)
}
